import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  BarChart3,
} from "lucide-react";

import { useStaffAuth } from "../../context/StaffAuthContext";

import {
  getTemplateById,
  updateTemplate,
  updateChapters,
} from "../../api/syllabus/syllabusApi";
import {
  getTemplateByIdPortal,
  updateTemplatePortal,
  updateChaptersPortal,
} from "../../api/syllabus/syllabusPortalApi";

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isOverdue = (date) => !!date && new Date(date) < new Date();

const toInputDate = (date) => (date ? new Date(date).toISOString().slice(0, 10) : "");

const emptyTopic = (topicNo) => ({
  topicNo,
  topicName: "",
  description: "",
  targetCompletionDate: "",
  estimatedPeriods: 1,
});

const emptyChapter = (chapterNo) => ({
  chapterNo,
  chapterName: "",
  description: "",
  targetCompletionDate: "",
  estimatedPeriods: 0,
  topics: [],
});

const SyllabusTemplateDetail = ({ isPortal = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();

  const canManage = isPortal ? !!staffAuth?.hasPermission?.("SYLLABUS_FULL") : true;

  const [template, setTemplate] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [dirty, setDirty] = useState(false);

  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({ title: "", academicYear: "", targetCompletionDate: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal ? await getTemplateByIdPortal(id) : await getTemplateById(id);
      const data = res.data || res;
      setTemplate(data);
      setChapters(data.chapters || []);
      setMetaForm({
        title: data.title || "",
        academicYear: data.academicYear || "",
        targetCompletionDate: toInputDate(data.targetCompletionDate),
      });
      // Default: first chapter expanded
      if (data.chapters?.length) setExpanded(new Set([data.chapters[0].chapterNo]));
      setDirty(false);
    } catch (err) {
      console.error("Failed to load template:", err);
    } finally {
      setLoading(false);
    }
  }, [id, isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExpand = (chapterNo) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(chapterNo)) next.delete(chapterNo);
      else next.add(chapterNo);
      return next;
    });
  };

  const backPath = isPortal ? "/staff-panel/syllabus" : "/syllabus";
  const overviewPath = isPortal
    ? `/staff-panel/syllabus/overview/${id}`
    : `/syllabus/overview/${id}`;

  const handleSaveMeta = async () => {
    setSaving(true);
    try {
      const payload = {
        title: metaForm.title,
        academicYear: metaForm.academicYear,
        targetCompletionDate: metaForm.targetCompletionDate || null,
      };
      const res = isPortal
        ? await updateTemplatePortal(id, payload)
        : await updateTemplate(id, payload);
      setTemplate(res.data || res);
      setEditingMeta(false);
    } catch (err) {
      console.error("Failed to update template meta:", err);
    } finally {
      setSaving(false);
    }
  };

  const updateChapterField = (chapterNo, field, value) => {
    setChapters((prev) =>
      prev.map((c) => (c.chapterNo === chapterNo ? { ...c, [field]: value } : c))
    );
    setDirty(true);
  };

  const updateTopicField = (chapterNo, topicNo, field, value) => {
    setChapters((prev) =>
      prev.map((c) =>
        c.chapterNo === chapterNo
          ? {
              ...c,
              topics: c.topics.map((t) =>
                t.topicNo === topicNo ? { ...t, [field]: value } : t
              ),
            }
          : c
      )
    );
    setDirty(true);
  };

  const addChapter = () => {
    const nextNo = chapters.length ? Math.max(...chapters.map((c) => c.chapterNo)) + 1 : 1;
    const newChapter = emptyChapter(nextNo);
    setChapters((prev) => [...prev, newChapter]);
    setExpanded((prev) => new Set(prev).add(nextNo));
    setDirty(true);
  };

  const deleteChapter = (chapterNo) => {
    setChapters((prev) => prev.filter((c) => c.chapterNo !== chapterNo));
    setDirty(true);
  };

  const addTopic = (chapterNo) => {
    setChapters((prev) =>
      prev.map((c) => {
        if (c.chapterNo !== chapterNo) return c;
        const nextTopicNo = c.topics.length
          ? Math.max(...c.topics.map((t) => t.topicNo)) + 1
          : 1;
        return { ...c, topics: [...c.topics, emptyTopic(nextTopicNo)] };
      })
    );
    setDirty(true);
  };

  const deleteTopic = (chapterNo, topicNo) => {
    setChapters((prev) =>
      prev.map((c) =>
        c.chapterNo === chapterNo
          ? { ...c, topics: c.topics.filter((t) => t.topicNo !== topicNo) }
          : c
      )
    );
    setDirty(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const res = isPortal
        ? await updateChaptersPortal(id, chapters)
        : await updateChapters(id, chapters);
      const data = res.data || res;
      setTemplate(data);
      setChapters(data.chapters || []);
      setDirty(false);
    } catch (err) {
      console.error("Failed to save chapters:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center text-neutral-400 dark:text-neutral-500 py-16">Loading template…</div>;
  }

  if (!template) {
    return <div className="text-center text-neutral-400 dark:text-neutral-500 py-16">Template not found</div>;
  }

  const subject = template.subjectId || {};
  const classInfo = template.classId || {};
  const overdue = isOverdue(template.targetCompletionDate);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button
        onClick={() => navigate(backPath)}
        className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:text-neutral-100 mb-4"
      >
        <ArrowLeft size={16} /> Back to Templates
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div className="flex-1 min-w-[240px]">
            {editingMeta ? (
              <div className="space-y-2">
                <input
                  value={metaForm.title}
                  onChange={(e) => setMetaForm((f) => ({ ...f, title: e.target.value }))}
                  className="border rounded-xl px-3 py-2 text-sm w-full font-bold"
                />
                <div className="flex gap-2">
                  <input
                    value={metaForm.academicYear}
                    onChange={(e) => setMetaForm((f) => ({ ...f, academicYear: e.target.value }))}
                    className="border rounded-xl px-3 py-2 text-sm w-32"
                    placeholder="2024-25"
                  />
                  <input
                    type="date"
                    value={metaForm.targetCompletionDate}
                    onChange={(e) =>
                      setMetaForm((f) => ({ ...f, targetCompletionDate: e.target.value }))
                    }
                    className="border rounded-xl px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleSaveMeta}
                    disabled={saving}
                    className="bg-violet-600 text-white px-3 py-2 rounded-xl text-sm flex items-center gap-1"
                  >
                    <Check size={14} /> Save
                  </button>
                  <button
                    onClick={() => setEditingMeta(false)}
                    className="bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 px-3 py-2 rounded-xl text-sm flex items-center gap-1"
                  >
                    <X size={14} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    className="text-xs px-2 py-0.5 rounded-lg text-white"
                    style={{ backgroundColor: subject.color || "#8b5cf6" }}
                  >
                    {subject.name || "Subject"}
                  </span>
                  <span className="bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 text-xs px-2 py-0.5 rounded-lg">
                    {classInfo.className || "Class"}
                  </span>
                  <span className="text-xs text-neutral-400 dark:text-neutral-500">{template.academicYear}</span>
                </div>
                <h1 className="text-xl font-bold text-neutral-800 dark:text-neutral-100">{template.title}</h1>
                {template.targetCompletionDate && (
                  <p className={`text-sm mt-1 ${overdue ? "text-red-500 font-medium" : "text-neutral-400 dark:text-neutral-500"}`}>
                    Target: {formatDate(template.targetCompletionDate)}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(overviewPath)}
              className="flex items-center gap-1 border px-3 py-2 rounded-xl text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              <BarChart3 size={16} /> Progress Overview
            </button>
            {canManage && !editingMeta && (
              <button
                onClick={() => setEditingMeta(true)}
                className="p-2 rounded-xl hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                title="Edit template details"
              >
                <Pencil size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Chapters */}
      {chapters.length === 0 ? (
        <div className="text-center text-neutral-400 dark:text-neutral-500 py-16 bg-white dark:bg-neutral-900 rounded-2xl shadow-sm">
          <p className="mb-3">No chapters added yet. Click "Add Chapter" to begin.</p>
          {canManage && (
            <button
              onClick={addChapter}
              className="bg-violet-600 text-white px-4 py-2 rounded-xl text-sm"
            >
              Add Chapter
            </button>
          )}
        </div>
      ) : (
        <div>
          {chapters.map((chapter) => {
            const chapterExpanded = expanded.has(chapter.chapterNo);
            const chapterOverdue = isOverdue(chapter.targetCompletionDate);

            return (
              <div key={chapter.chapterNo} className="border rounded-2xl mb-4 bg-white dark:bg-neutral-900">
                <div
                  className="flex justify-between items-center p-4 cursor-pointer"
                  onClick={() => toggleExpand(chapter.chapterNo)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {chapterExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <span className="bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 px-2 py-1 rounded-lg text-sm font-mono">
                      Ch {chapter.chapterNo}
                    </span>
                    {canManage ? (
                      <input
                        value={chapter.chapterName}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          updateChapterField(chapter.chapterNo, "chapterName", e.target.value)
                        }
                        placeholder="Chapter name"
                        className="font-bold border-b border-transparent hover:border-neutral-200 dark:border-neutral-800 focus:border-violet-400 outline-none bg-transparent"
                      />
                    ) : (
                      <h3 className="font-bold">{chapter.chapterName}</h3>
                    )}
                  </div>

                  <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                    {canManage ? (
                      <input
                        type="date"
                        value={toInputDate(chapter.targetCompletionDate)}
                        onChange={(e) =>
                          updateChapterField(chapter.chapterNo, "targetCompletionDate", e.target.value)
                        }
                        className="text-xs border rounded-lg px-2 py-1"
                      />
                    ) : (
                      chapter.targetCompletionDate && (
                        <span className={`text-xs ${chapterOverdue ? "text-red-500" : "text-neutral-400 dark:text-neutral-500"}`}>
                          Target: {formatDate(chapter.targetCompletionDate)}
                        </span>
                      )
                    )}

                    {!canManage && (
                      <button
                        onClick={() => navigate(overviewPath)}
                        className="text-xs border px-2 py-1 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      >
                        View Progress
                      </button>
                    )}

                    {canManage && (
                      <button
                        onClick={() => deleteChapter(chapter.chapterNo)}
                        className="text-red-400 hover:bg-red-50 p-1.5 rounded-lg"
                        title="Delete chapter"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {chapterExpanded && (
                  <div className="px-4 pb-4">
                    {chapter.description !== undefined && canManage && (
                      <input
                        value={chapter.description}
                        onChange={(e) =>
                          updateChapterField(chapter.chapterNo, "description", e.target.value)
                        }
                        placeholder="Chapter description (optional)"
                        className="w-full text-sm border rounded-xl px-3 py-2 mb-3 text-neutral-600 dark:text-neutral-300"
                      />
                    )}

                    {chapter.topics.length === 0 ? (
                      <p className="text-sm text-neutral-400 dark:text-neutral-500 pl-8">No topics yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {chapter.topics.map((topic) => {
                          const topicOverdue = isOverdue(topic.targetCompletionDate);
                          return (
                            <div
                              key={topic.topicNo}
                              className="flex items-center justify-between pl-8 pr-2 py-2 border-b last:border-b-0"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <span className="text-xs text-neutral-400 dark:text-neutral-500 font-mono w-6">
                                  {topic.topicNo}.
                                </span>
                                {canManage ? (
                                  <input
                                    value={topic.topicName}
                                    onChange={(e) =>
                                      updateTopicField(
                                        chapter.chapterNo,
                                        topic.topicNo,
                                        "topicName",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Topic name"
                                    className="text-sm border-b border-transparent hover:border-neutral-200 dark:border-neutral-800 focus:border-violet-400 outline-none bg-transparent flex-1"
                                  />
                                ) : (
                                  <p className="text-sm">{topic.topicName}</p>
                                )}
                              </div>

                              <div className="flex items-center gap-2 text-xs text-neutral-400 dark:text-neutral-500">
                                {canManage ? (
                                  <>
                                    <input
                                      type="date"
                                      value={toInputDate(topic.targetCompletionDate)}
                                      onChange={(e) =>
                                        updateTopicField(
                                          chapter.chapterNo,
                                          topic.topicNo,
                                          "targetCompletionDate",
                                          e.target.value
                                        )
                                      }
                                      className="border rounded-lg px-1.5 py-1"
                                    />
                                    <input
                                      type="number"
                                      min={0}
                                      value={topic.estimatedPeriods}
                                      onChange={(e) =>
                                        updateTopicField(
                                          chapter.chapterNo,
                                          topic.topicNo,
                                          "estimatedPeriods",
                                          Number(e.target.value)
                                        )
                                      }
                                      className="border rounded-lg px-1.5 py-1 w-14"
                                    />
                                    <button
                                      onClick={() => deleteTopic(chapter.chapterNo, topic.topicNo)}
                                      className="text-red-400 hover:bg-red-50 p-1 rounded-lg"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {topic.targetCompletionDate && (
                                      <span className={topicOverdue ? "text-red-500" : ""}>
                                        Target: {formatDate(topic.targetCompletionDate)}
                                      </span>
                                    )}
                                    <span>Estimated: {topic.estimatedPeriods} periods</span>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {canManage && (
                      <button
                        onClick={() => addTopic(chapter.chapterNo)}
                        className="mt-3 ml-8 flex items-center gap-1 text-xs text-violet-600 hover:underline"
                      >
                        <Plus size={14} /> Add Topic
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {canManage && (
            <button
              onClick={addChapter}
              className="w-full border-2 border-dashed rounded-2xl py-3 text-sm text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 flex items-center justify-center gap-1 mb-4"
            >
              <Plus size={16} /> Add Chapter
            </button>
          )}
        </div>
      )}

      {canManage && dirty && (
        <div className="sticky bottom-4 flex justify-end">
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="bg-violet-600 text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:bg-violet-700 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save All Changes"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SyllabusTemplateDetail;